import React from 'react';
import { Collapse, Typography, Row, Col } from 'antd';
import { FormFieldRenderer } from '../form-field-renderer';
import { useFormConfig } from '../../../hooks/banners/creation/form/use-form-config';
import { MultiFieldProps } from '../../../types/form-renderer';
import { AccordionConfig } from '../../../types/accordion';
import { useResponsiveSizes } from '../../../hooks/use-responsive-sizes';

const { Text } = Typography;
const { Panel } = Collapse;

export const AccordionFormRenderer: React.FC<MultiFieldProps> = ({
  fields,
  formData,
  onFieldChange,
  fieldWidth,
  errors,
  creativityType
}) => {
  const { accordionConfig, fieldMap } = useFormConfig(fields, fieldWidth);
  const { fontSize } = useResponsiveSizes();

  const defaultActiveKeys =
    accordionConfig
      ?.filter((config: AccordionConfig) => config.defaultOpen)
      .map((config: AccordionConfig) => config.key) || [];

  if (!accordionConfig) {
    return (
      <div style={{ marginTop: 32, width: '100%', maxWidth: fieldWidth }}>
        {fields.map(field => (
          <FormFieldRenderer
            key={field.key}
            field={field}
            formData={formData}
            onFieldChange={onFieldChange}
            fieldWidth={fieldWidth}
            errors={errors}
            creativityType={creativityType}
          />
        ))}
      </div>
    );
  }

  const renderRegionGrid = (config: AccordionConfig) => {
    // Calcular regiones dinámicamente
    const regionFields = config.fields.filter((field: string) => field.startsWith('region'));
    const regionNumbers: number[] = regionFields.map((field: string) => parseInt(field.replace('region', '')));

    return (
      <Row gutter={[24, 16]}>
        {/* Switch de sincronización */}
        <Col xs={24} lg={24}>
          <FormFieldRenderer
            field={fieldMap['autoSyncTimes']}
            formData={formData}
            onFieldChange={onFieldChange}
            fieldWidth={fieldWidth}
            errors={errors}
            creativityType={creativityType}
          />
        </Col>
        <Col span={0}></Col>

        {/* Regiones dinámicas */}
        {regionNumbers.map(i => {
          const regionField = fieldMap[`region${i}`];
          const timeField = fieldMap[`time${i}`];
          const enableField = fieldMap[`enableRegion${i}`];

          if (!regionField || !timeField) return null;

          return (
            <Col key={i} span={12}>
              <div
                style={{
                  padding: '16px',
                  backgroundColor: 'white',
                  borderRadius: '6px',
                  border: '1px solid #f0f0f0'
                }}
              >
                {/* Título con switch integrado */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '12px'
                  }}
                >
                  <Text strong>{i}</Text>
                  {enableField && (
                    <div style={{ transform: 'scale(0.8)' }}>
                      <FormFieldRenderer
                        field={enableField}
                        formData={formData}
                        onFieldChange={onFieldChange}
                        fieldWidth={50}
                        errors={errors}
                        creativityType={creativityType}
                      />
                    </div>
                  )}
                </div>

                <FormFieldRenderer
                  field={regionField}
                  formData={formData}
                  onFieldChange={onFieldChange}
                  fieldWidth={fieldWidth * 0.4}
                  errors={errors}
                  creativityType={creativityType}
                />
                <div style={{ marginTop: '12px' }}>
                  <FormFieldRenderer
                    field={timeField}
                    formData={formData}
                    onFieldChange={onFieldChange}
                    fieldWidth={fieldWidth * 0.4}
                    errors={errors}
                    creativityType={creativityType}
                  />
                </div>
              </div>
            </Col>
          );
        })}
      </Row>
    );
  };

  const renderNormalLayout = (groupFields: any[]) => (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {groupFields.map(field => (
        <div key={field.key} style={{ width: '100%', maxWidth: fieldWidth }}>
          <FormFieldRenderer
            field={field}
            formData={formData}
            onFieldChange={onFieldChange}
            fieldWidth={fieldWidth}
            errors={errors}
            creativityType={creativityType}
          />
        </div>
      ))}
    </div>
  );

  return (
    <div style={{ marginTop: 32, width: fieldWidth }}>
      <Collapse defaultActiveKey={defaultActiveKeys} style={{ backgroundColor: 'transparent' }} ghost>
        {accordionConfig.map((config: AccordionConfig) => {
          const groupFields = config.fields.map(fieldKey => fieldMap[fieldKey]).filter(Boolean);

          if (groupFields.length === 0) return null;

          return (
            <Panel
              key={config.key}
              header={
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div
                    style={{
                      width: '4px',
                      height: '20px',
                      backgroundColor: config.color,
                      borderRadius: '2px'
                    }}
                  />
                  <Text strong style={{ fontSize: fontSize.lg, color: '#262626' }}>
                    {config.title}
                  </Text>
                </div>
              }
              style={{
                marginBottom: '16px',
                backgroundColor: '#fafafa',
                borderRadius: '8px',
                border: '1px solid #f0f0f0'
              }}
            >
              {config.layout === 'grid' && config.key === 'regions'
                ? renderRegionGrid(config)
                : renderNormalLayout(groupFields)}
            </Panel>
          );
        })}
      </Collapse>
    </div>
  );
};
